import React, { useCallback, useMemo } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import Tabs from '../../../../components/display/Tabs';
import { ActionsPage } from '../../workOrders/GeneralView/ActionsPage';
import { MultimediaPage } from '../../workOrders/GeneralView/MultimediaPage';

// Types
import { tabsType } from '../../../../state/reducers/ui/tabs';
import { tableIds } from '../../../../components/display/tables/types';

// Styles
import { ContainerStyled } from './styles';

// ═══════════════════════════════════════════════════════════════════
// TAB PATH HELPERS
// ═══════════════════════════════════════════════════════════════════
// Fase 3 nav Trabajo/Revisión (rework, 2026-07-12) — REWORK del diseño: las
// pestañas Actuacions/Multimedia NO van en el detalle de cada RV (revert de
// 8e433e04, ver commit de revert de esta misma tanda) — van en el LISTADO
// del módulo, igual que Órdenes de Trabajo tiene sus 5 pestañas en su vista
// general (`GeneralViewPage`, `/work-orders`). Semántica: "todas las
// actuaciones/multimedia de recorridos de vigilancia" = filtro por TIPO de
// origen (`containerTypes: ['surveillanceRoute']`), NO por un RV concreto.
// El listado de Campañas hace lo mismo con `['campaign']`. El detalle de cada RV
// (`RouteDetailedPage`) conserva sus 2 tabs de siempre (Vista prèvia +
// Ordres associades), sin tocar.
const TAB_PATHS_BASE = '/surveillance-route';

const TAB_PATHS = [
  TAB_PATHS_BASE,
  `${TAB_PATHS_BASE}/actions`,
  `${TAB_PATHS_BASE}/multimedia-list`,
];

const getIndexFromPath = (pathname: string): number => {
  const normalized = pathname.replace(/\/$/, '');
  const idx = TAB_PATHS.indexOf(normalized);
  return idx >= 0 ? idx : 0;
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function GeneralViewRoutePage(): JSX.Element {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const location = useLocation();

  // URL is the source of truth for the active tab on mount/reload
  const urlIndex = getIndexFromPath(location.pathname);

  const dataTab = useMemo(
    () => [
      { label: t('surveillance.generalView') },
      { label: t('workOrders.actions') },
      { label: t('workOrders.multimedia') },
    ],
    [t]
  );

  // Navigate when the selected tab changes (via click).
  // On mount/reload this is a no-op because the URL already matches.
  const handleTabChange = useCallback(
    (newIndex: number) => {
      const currentPath = location.pathname.replace(/\/$/, '');
      const targetPath = TAB_PATHS[newIndex] || TAB_PATHS[0];

      if (currentPath !== targetPath) {
        navigate(targetPath);
      }
    },
    [location.pathname, navigate]
  );

  return (
    <ContainerStyled>
      <Tabs
        data={dataTab}
        typeTab={tabsType.SURVEILLANCE_ROUTE_GENERAL}
        defaultIndex={urlIndex}
        setSelectedIndexTab={handleTabChange}
      />
      <Outlet />
    </ContainerStyled>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CONTEXTUAL TAB BINDINGS (Fase 3 nav Trabajo/Revisión, rework, 2026-07-12)
// ═══════════════════════════════════════════════════════════════════
// Las 2 páginas globales de entidad (Fase 2) que el modelo permite como
// hijas de un RV se parametrizan con `containerTypes` (filtro por TIPO de
// origen) en vez de `containerId` (no hay ningún `:id` de la URL — estas 2
// rutas son ESTÁTICAS, hermanas de `:routeId`, sin parámetro). Model-aware:
// SIN Accidentes ni Comunicaciones (el RV no las admite como hijas,
// hierarchy-validator.ts:24-25) — no hay `RouteAccidentsTab`/
// `RouteCommunicationsTab`, es intencional (mismo criterio que el commit
// revertido 8e433e04, ahora aplicado a nivel de módulo en vez de detalle).
const RV_CONTAINER_TYPES = ['surveillanceRoute'];

export function RouteActionsTab(): JSX.Element {
  return (
    <ActionsPage
      containerTypes={RV_CONTAINER_TYPES}
      tableId={tableIds.RV_ACTIONS}
      origin="rv-actions-tab"
      returnTo={`${TAB_PATHS_BASE}/actions`}
    />
  );
}

export function RouteMultimediaTab(): JSX.Element {
  return (
    <MultimediaPage
      containerTypes={RV_CONTAINER_TYPES}
      tableId={tableIds.RV_MULTIMEDIA}
      origin="rv-multimedia-tab"
      returnTo={`${TAB_PATHS_BASE}/multimedia-list`}
    />
  );
}
